//Mar 11 SmartListsSidePanel.jsx (updated with drag-and-drop)
// src/components/SmartLists/SmartListsSidePanel.jsx
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Tooltip,
} from '@mui/material';
import ArrowForwardIosSharpIcon from '@mui/icons-material/ArrowForwardIosSharp';
import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft';
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';
import AddIcon from '@mui/icons-material/Add';
import { useLocation } from 'react-router-dom';
import { safeGetItem, safeSetItem } from '../../utils/storageUtils';

import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';

/**
 * SmartListsSidePanel
 * -----------------------------------------------------------------------------
 * Provider-safe sidepanel shell for the SmartLists workspace.
 *
 * DRAG-AND-DROP ADDITION
 *  • Enables reordering of SmartList rows inside the accordion
 *  • Row identity is derived from `localDraftId`
 *  • Updated order is communicated via onRowReorder callback
 *
 * WHY THIS EXISTS
 *  • The existing builder SidePanel.jsx is tightly coupled to builder-only providers
 *  • /settings/smartlists is page-local and should remain provider-safe
 *
 * CURRENT SCOPE
 *  • Supports two modes: 'list' | 'settings'
 *  • Persists accordion expansion state via storageUtils
 *  • Supports collapsed mini-rail UI
 * -----------------------------------------------------------------------------
 */

const TOPBAR_BG = 'rgba(213, 228, 249, 0.5)';
const TOPBAR_HEIGHT = 50;
const TOPBAR_ICON_COLOR = '#003459';
const SIDEPANEL_BORDER = '1px solid #e0e0e0';

const SmartListsSidePanel = ({
  mode = 'list',                 // 'list' | 'settings'
  sections = [],                 // list-mode sections
  settingsContent = null,        // settings-mode content
  topBar = null,                 // top bar element (list mode or settings mode)
  width = '30%',
  minWidth = '30%',
  maxWidth = '30%',
  collapsedWidth = 56,
  storageKeyPrefix = 'smartlists_sidepanel_expanded',
  isCollapsed: controlledIsCollapsed = undefined,
  onToggleCollapse = null,
  onCreateNewField = null,
  collapsedDirtyBadgeVisible = false,
  /**
   * New prop: row reorder handler
   * ---------------------------------------------------------------------------
   * Called when a row is dragged and dropped to a new position
   * Receives an object: { sectionIndex, sourceIndex, destinationIndex }
   * Parent component (SmartListsPage.jsx) should update workspaceItems accordingly
   */
  onRowReorder = null,
}) => {
  const location = useLocation();

  const [expanded, setExpanded] = useState({});
  const [hasHydratedExpanded, setHasHydratedExpanded] = useState(false);

  // ---------------------------------------------------------------------------
  // Collapse state (controlled or uncontrolled)
  // ---------------------------------------------------------------------------
  const [uncontrolledIsCollapsed, setUncontrolledIsCollapsed] = useState(false);

  const isCollapsed =
    typeof controlledIsCollapsed === 'boolean'
      ? controlledIsCollapsed
      : uncontrolledIsCollapsed;

  const handleToggleCollapse = useCallback(() => {
    if (typeof onToggleCollapse === 'function') {
      onToggleCollapse(!isCollapsed);
      return;
    }

    setUncontrolledIsCollapsed((prev) => !prev);
  }, [isCollapsed, onToggleCollapse]);

  const handleCreateNewFieldClick = useCallback(() => {
    if (typeof onCreateNewField === 'function') {
      onCreateNewField();
    }
  }, [onCreateNewField]);

  // Scope accordion persistence to the current route path
  const expandedStorageKey = useMemo(() => {
    const path = (location?.pathname || '').replace(/\//g, '_') || 'unknown';
    return `${storageKeyPrefix}_${path}`;
  }, [location?.pathname, storageKeyPrefix]);

  // Hydrate accordion state for list mode only
  useEffect(() => {
    if (mode !== 'list') return;

    if (!Array.isArray(sections) || sections.length === 0) {
      return;
    }

    const currentPanelKeys = [];
    sections.forEach((section, index) => {
      if (!section?.noAccordion) {
        currentPanelKeys.push(`panel${index}`);
      }
    });

    const saved = safeGetItem(expandedStorageKey);

    const savedMap =
      saved && typeof saved === 'object' && !Array.isArray(saved)
        ? saved
        : null;

    const nextExpanded = savedMap ? { ...savedMap } : {};

    // New panels open by default
    currentPanelKeys.forEach((panelKey) => {
      if (typeof nextExpanded[panelKey] !== 'boolean') {
        nextExpanded[panelKey] = true;
      }
    });

    // Clean out panels that no longer exist
    Object.keys(nextExpanded).forEach((panelKey) => {
      if (!currentPanelKeys.includes(panelKey)) {
        delete nextExpanded[panelKey];
      }
    });

    setExpanded(nextExpanded);
    setHasHydratedExpanded(true);
  }, [mode, sections, expandedStorageKey]);

  // Persist accordion state after hydration
  useEffect(() => {
    if (mode !== 'list') return;
    if (!hasHydratedExpanded) return;

    safeSetItem(expandedStorageKey, expanded);
  }, [mode, expanded, expandedStorageKey, hasHydratedExpanded]);

  const handleChange = (panel) => (event, isExpanded) => {
    setExpanded((prev) => ({
      ...prev,
      [panel]: isExpanded ? true : false,
    }));
  };

  // ---------------------------------------------------------------------------
  // Drag-and-drop handler
  // ---------------------------------------------------------------------------
  const handleDragEnd = useCallback(
    (result, sectionIndex) => {
      const { source, destination } = result;

      if (!destination) return;

      console.log('[SmartListsSidePanel] Drag ended:', { sectionIndex, source, destination });

      if (typeof onRowReorder === 'function') {
        onRowReorder({
          sectionIndex,
          sourceIndex: source.index,
          destinationIndex: destination.index,
        });
      }
    },
    [onRowReorder]
  );

  // ---------------------------------------------------------------------------
  // Collapsed rail UI
  // ---------------------------------------------------------------------------
  if (isCollapsed) {
    return (
      <Box
        sx={{
          minWidth: `${collapsedWidth}px`,
          maxWidth: `${collapsedWidth}px`,
          width: `${collapsedWidth}px`,
          bgcolor: TOPBAR_BG,
          position: 'relative',
          height: '100%',
          minHeight: 0,
          p: 0,
          display: 'flex',
          flexDirection: 'column',
          flexShrink: 0,
          overflow: 'hidden',
          borderRight: SIDEPANEL_BORDER,
          boxSizing: 'border-box',
        }}
      >
        {collapsedDirtyBadgeVisible ? (
          <Box
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              width: 8,
              height: 8,
              borderRadius: '50%',
              backgroundColor: '#d97706',
              boxShadow: '0 0 0 2px rgba(255,255,255,0.9)',
              zIndex: 2,
            }}
          />
        ) : null}

        <Box
          sx={{
            minHeight: `${TOPBAR_HEIGHT}px`,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'flex-start',
            pt: 0.75,
            pb: 0.5,
            backgroundColor: TOPBAR_BG,
            gap: 0.5,
          }}
        >
          <Tooltip title="Expand sidebar" arrow placement="right">
            <IconButton
              onClick={handleToggleCollapse}
              aria-label="Expand sidebar"
              sx={{
                color: TOPBAR_ICON_COLOR,
                '&:hover': {
                  backgroundColor: 'transparent',
                },
              }}
            >
              <KeyboardDoubleArrowRightIcon sx={{ fontSize: '1rem' }} />
            </IconButton>
          </Tooltip>

          <Tooltip title="Add new field" arrow placement="right">
            <IconButton
              onClick={handleCreateNewFieldClick}
              aria-label="Add new field"
              sx={{
                color: TOPBAR_ICON_COLOR,
                '&:hover': {
                  backgroundColor: 'transparent',
                },
              }}
            >
              <AddIcon sx={{ fontSize: '1rem' }} />
            </IconButton>
          </Tooltip>
        </Box>

        <Box
          sx={{
            flex: 1,
            overflow: 'hidden',
            backgroundColor: TOPBAR_BG,
          }}
        />
      </Box>
    );
  }

  // ---------------------------------------------------------------------------
  // Expanded mode
  // ---------------------------------------------------------------------------
  return (
    <Box
      sx={{
        minWidth,
        maxWidth,
        width,
        bgcolor: 'background.paper',
        height: '100%',
        minHeight: 0,
        p: 0,
        display: 'flex',
        flexDirection: 'column',
        flexShrink: 0,
        overflow: 'hidden',
        borderRight: SIDEPANEL_BORDER,
        boxSizing: 'border-box',
      }}
    >
      {/* ---------------------------------------------------------------------
       * Expanded top bar
       * ------------------------------------------------------------------- */}
      <Box
        sx={{
          minHeight: `${TOPBAR_HEIGHT}px`,
          height: `${TOPBAR_HEIGHT}px`,
          backgroundColor: TOPBAR_BG,
          borderBottom: SIDEPANEL_BORDER,
          display: 'flex',
          alignItems: 'stretch',
          width: '100%',
          boxSizing: 'border-box',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            alignItems: 'stretch',
            backgroundColor: 'transparent',
            boxSizing: 'border-box',
            '& > *': {
              flex: 1,
              minWidth: 0,
              backgroundColor: 'transparent !important',
              borderBottom: 'none !important',
              boxSizing: 'border-box',
            },
          }}
        >
          {topBar}
        </Box>

        <Box
          sx={{
            flex: '0 0 auto',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            pr: 0.5,
            pl: 0,
            backgroundColor: 'transparent',
            boxSizing: 'border-box',
          }}
        >
          <Tooltip title="Collapse sidebar" arrow placement="left">
            <IconButton
              onClick={handleToggleCollapse}
              aria-label="Collapse sidebar"
              sx={{
                color: TOPBAR_ICON_COLOR,
                '&:hover': {
                  backgroundColor: 'transparent',
                },
              }}
            >
              <KeyboardDoubleArrowLeftIcon sx={{ fontSize: '1rem' }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {mode === 'settings' ? (
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            overflowX: 'hidden',
            pb: 2,
          }}
        >
          {settingsContent || (
            <Box sx={{ p: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Select a SmartList field to edit.
              </Typography>
            </Box>
          )}
        </Box>
      ) : (
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            overflowX: 'hidden',
            pb: 2,
          }}
        >
          {sections.map((section, index) => {
            if (section.noAccordion) {
              return (
                <Box key={index} sx={{ p: section.noPadding ? 0 : 2 }}>
                  <DragDropContext
                    onDragEnd={(result) => handleDragEnd(result, index)}
                  >
                    <Droppable droppableId={`section-${index}`}>
                      {(provided) => (
                        <Box
                          ref={provided.innerRef}
                          {...provided.droppableProps}
                        >
                          {section.items.map((item, idx) => (
                            <Draggable
                              key={item.localDraftId || idx}
                              draggableId={item.localDraftId || `idx-${idx}`}
                              index={idx}
                            >
                              {(draggableProvided, snapshot) => (
                                <Box
                                  ref={draggableProvided.innerRef}
                                  {...draggableProvided.draggableProps}
                                  {...draggableProvided.dragHandleProps}
                                  sx={{
                                    width: '100%',
                                    p: section.noPadding ? 0 : '4px 8px',
                                    borderBottom: '0px solid #e0e0e0',
                                    opacity: snapshot.isDragging ? 0.8 : 1,
                                    backgroundColor: snapshot.isDragging
                                      ? 'rgba(25, 118, 210, 0.08)'
                                      : 'transparent',
                                  }}
                                >
                                  {item.element}
                                </Box>
                              )}
                            </Draggable>
                          ))}
                          {provided.placeholder}
                        </Box>
                      )}
                    </Droppable>
                  </DragDropContext>
                </Box>
              );
            }

            return (
              <Accordion
                key={index}
                expanded={expanded[`panel${index}`] || false}
                onChange={handleChange(`panel${index}`)}
                disableGutters
                square
                sx={{
                  m: 0,
                  borderBottom: 'none',
                  '&:before': { display: 'none' },
                }}
              >
                <AccordionSummary
                  expandIcon={
                    <ArrowForwardIosSharpIcon
                      sx={{
                        fontSize: '0.8rem',
                        transform: `rotate(${expanded[`panel${index}`] ? '-90deg' : '0deg'})`,
                      }}
                    />
                  }
                  sx={{
                    flexDirection: 'row-reverse',
                    justifyContent: 'space-between',
                    m: 0,
                    py: 1,
                    px: 1,
                    minHeight: 32,
                    '&.Mui-expanded': { minHeight: 32 },
                    '& .MuiAccordionSummary-content': { margin: 0 },
                    '& .MuiAccordionSummary-content.Mui-expanded': { margin: 0 },
                  }}
                >
                  <Typography
                    sx={{ fontSize: '0.8rem', fontWeight: 'bold', p: '0px 16px' }}
                  >
                    {section.title}
                  </Typography>
                </AccordionSummary>

                <AccordionDetails
                  sx={{
                    p: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    '& > :last-child': { pb: 2 },
                  }}
                >
                  <DragDropContext
                    onDragEnd={(result) => handleDragEnd(result, index)}
                  >
                    <Droppable droppableId={`section-${index}`}>
                      {(provided) => (
                        <Box
                          ref={provided.innerRef}
                          {...provided.droppableProps}
                        >
                          {section.items.map((item, idx) => (
                            <Draggable
                              key={item.localDraftId || idx}
                              draggableId={item.localDraftId || `idx-${idx}`}
                              index={idx}
                            >
                              {(draggableProvided, snapshot) => (
                                <Box
                                  ref={draggableProvided.innerRef}
                                  {...draggableProvided.draggableProps}
                                  {...draggableProvided.dragHandleProps}
                                  sx={{
                                    width: '100%',
                                    p: '4px 8px',
                                    borderBottom: '0px solid #e0e0e0',
                                    opacity: snapshot.isDragging ? 0.8 : 1,
                                    backgroundColor: snapshot.isDragging
                                      ? 'rgba(25, 118, 210, 0.08)'
                                      : 'transparent',
                                  }}
                                >
                                  {item.element}
                                </Box>
                              )}
                            </Draggable>
                          ))}
                          {provided.placeholder}
                        </Box>
                      )}
                    </Droppable>
                  </DragDropContext>
                </AccordionDetails>
              </Accordion>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

export default SmartListsSidePanel;